import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  applyCasaMapaListingAttributeTags,
  fetchCasaMapaListingAttributeTags,
} from "../../../api/clientCasamapaAdminApi";
import { casamapaAdminPortalPath } from "../../../constants/casamapaAdminPortalPaths";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale } from "../../../intl";
import "../../../styles/casamapaAdminPortal.css";

const COPY = "realEstate.casaMapa.admin.attributeTags";

function isSpanishLocale(locale) {
  return String(locale || "").toLowerCase().startsWith("es");
}

function formatPercent(count, total) {
  if (!total) return "0%";
  return `${Math.round((Number(count) / Number(total)) * 100)}%`;
}

export default function CasaMapaAdminAttributeTagsPage() {
  const { clientViewId } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const spanish = isSpanishLocale(locale);
  const [mode, setMode] = useState("live");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);
  const loadErrorFallback = t(`${COPY}.loadError`);
  const applyErrorFallback = t(`${COPY}.applyError`);

  useEffect(() => {
    if (!clientViewId) {
      setLoading(false);
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchCasaMapaListingAttributeTags(clientViewId, { mode })
      .then((result) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setData(result);
      })
      .catch((err) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setError(err?.message || loadErrorFallback);
        setData(null);
      })
      .finally(() => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId, mode, reloadToken, loadErrorFallback]);

  const load = () => setReloadToken((value) => value + 1);

  const handleApply = async () => {
    if (!clientViewId) return;
    setApplying(true);
    setError("");
    setNotice("");
    try {
      const result = await applyCasaMapaListingAttributeTags(clientViewId);
      setData(result.report || null);
      setMode("stored");
      setNotice(
        t(`${COPY}.applied`, {
          updated: result.updatedListings || 0,
          tagged: result.taggedListings || 0,
        })
      );
    } catch (err) {
      setError(err?.message || applyErrorFallback);
    } finally {
      setApplying(false);
    }
  };

  const categories = useMemo(() => data?.categories || [], [data]);
  const totalActive = data?.totalActiveListings || 0;

  if (!clientViewId) {
    return <p className="muted">Select a CasaMapa client view to continue.</p>;
  }

  return (
    <div className="casamapa-admin-page cm-attr-tags">
      <header className="casamapa-admin-page__header">
        <div>
          <p className="casamapa-admin-eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="muted">{t(`${COPY}.subtitle`)}</p>
        </div>
        <div className="casamapa-admin-page__actions">
          <Link className="btn btn-secondary" to={casamapaAdminPortalPath("public-inventory")}>
            {t(`${COPY}.backToInventory`)}
          </Link>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading || applying}>
            {t(`${COPY}.refresh`)}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply} disabled={applying || loading}>
            {applying ? t(`${COPY}.applying`) : t(`${COPY}.apply`)}
          </button>
        </div>
      </header>

      <div className="cm-attr-tags__tabs" role="tablist" aria-label={t(`${COPY}.modeLabel`)}>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "live"}
          className={`cm-attr-tags__tab${mode === "live" ? " is-active" : ""}`}
          onClick={() => setMode("live")}
        >
          {t(`${COPY}.modeLive`)}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "stored"}
          className={`cm-attr-tags__tab${mode === "stored" ? " is-active" : ""}`}
          onClick={() => setMode("stored")}
        >
          {t(`${COPY}.modeStored`)}
        </button>
      </div>

      {error ? <p className="casamapa-admin-error">{error}</p> : null}
      {notice ? <p className="casamapa-admin-notice">{notice}</p> : null}

      {loading && !data ? (
        <p className="muted">{t(`${COPY}.loading`)}</p>
      ) : (
        <>
          <section className="cm-attr-tags__metrics" aria-label={t(`${COPY}.summary`)}>
            <article className="panel-card cm-attr-tags__metric">
              <p className="muted">{t(`${COPY}.scanned`)}</p>
              <strong>{(data?.scannedListings || 0).toLocaleString(locale)}</strong>
            </article>
            <article className="panel-card cm-attr-tags__metric">
              <p className="muted">{t(`${COPY}.taggedListings`)}</p>
              <strong>{(data?.taggedListings || 0).toLocaleString(locale)}</strong>
            </article>
            <article className="panel-card cm-attr-tags__metric">
              <p className="muted">{t(`${COPY}.activeTags`)}</p>
              <strong>{(data?.tagCount || 0).toLocaleString(locale)}</strong>
            </article>
            <article className="panel-card cm-attr-tags__metric">
              <p className="muted">{t(`${COPY}.coverage`)}</p>
              <strong>{formatPercent(data?.taggedListings || 0, totalActive)}</strong>
            </article>
          </section>

          <p className="muted cm-attr-tags__hint">
            {mode === "live" ? t(`${COPY}.liveHint`) : t(`${COPY}.storedHint`)}
          </p>

          {categories.map((category) => {
            const categoryLabel = spanish ? category.labelEs : category.labelEn;
            return (
              <section key={category.key} className="panel-card cm-attr-tags__category">
                <header className="cm-attr-tags__category-header">
                  <div>
                    <h2>{categoryLabel}</h2>
                    <p className="muted">
                      {t(`${COPY}.categoryMeta`, {
                        tags: category.tagCount,
                        listings: category.listingCount,
                      })}
                    </p>
                  </div>
                </header>
                <div className="cm-attr-tags__table-wrap">
                  <table className="cm-attr-tags__table">
                    <thead>
                      <tr>
                        <th>{t(`${COPY}.columns.tag`)}</th>
                        <th>{t(`${COPY}.columns.listings`)}</th>
                        <th>{t(`${COPY}.columns.share`)}</th>
                        <th>{t(`${COPY}.columns.samples`)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(category.tags || []).map((tag) => {
                        const label = spanish ? tag.labelEs : tag.labelEn;
                        return (
                          <tr key={tag.key} className={tag.listingCount ? "" : "is-empty"}>
                            <td>
                              <strong>{label}</strong>
                              <div className="muted cm-attr-tags__key">{tag.key}</div>
                            </td>
                            <td>{tag.listingCount.toLocaleString(locale)}</td>
                            <td>{formatPercent(tag.listingCount, totalActive)}</td>
                            <td>
                              {tag.sampleTitles?.length ? (
                                <ul className="cm-attr-tags__samples">
                                  {tag.sampleTitles.map((title) => (
                                    <li key={title}>{title}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
